import { Link } from 'react-router-dom';
import { MapPin, Clock, Phone } from 'lucide-react';
import { cafeData } from '../data/cafeData';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#3A4D39] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6" data-testid="footer-logo">
              <div className="bg-white p-3 rounded-md inline-block">
                <img 
                  src={cafeData.logo} 
                  alt="Cafe 110 Logo" 
                  className="h-14 w-auto"
                />
              </div>
            </Link>
            <p className="text-white/80 text-sm leading-relaxed mb-6">
              A family-run Italian café bringing authentic Calabrian flavours and community warmth to the heart of South Wimbledon.
            </p>
            {/* Hygiene Badge */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white text-xl font-bold" data-testid="footer-hygiene-badge">
                {cafeData.hygiene.rating}
              </div>
              <div className="text-sm">
                <p className="font-medium">Food Hygiene Rating</p>
                <p className="text-white/70 text-xs">Inspected {cafeData.hygiene.inspectionDate}</p>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-6">Quick Links</h4>
            <nav className="flex flex-col gap-3">
              <Link to="/" className="text-white/80 hover:text-white transition-colors text-sm" data-testid="footer-link-home">Home</Link>
              <Link to="/menu" className="text-white/80 hover:text-white transition-colors text-sm" data-testid="footer-link-menu">Menu</Link>
              <Link to="/about" className="text-white/80 hover:text-white transition-colors text-sm" data-testid="footer-link-about">About Us</Link>
              <Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm" data-testid="footer-link-reviews">Reviews</Link>
              <Link to="/contact" className="text-white/80 hover:text-white transition-colors text-sm" data-testid="footer-link-contact">Contact</Link>
            </nav>
          </div>

          {/* Opening Hours */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-6 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Opening Hours
            </h4>
            <ul className="space-y-2 text-sm">
              {cafeData.hours.map((item) => (
                <li key={item.day} className="flex justify-between text-white/80">
                  <span>{item.day}</span>
                  <span>{item.open} - {item.close}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-6">Find Us</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-white/80">
                  <p>{cafeData.address.street}</p>
                  <p>{cafeData.address.area}</p>
                  <p>{cafeData.address.postcode}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm text-white/80">Near {cafeData.nearestStation} Station</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/60 text-sm">
            © {currentYear} {cafeData.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-sm text-white/60">
            <a href={cafeData.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="footer-directions-link">
              Get Directions
            </a>
          </div>
        </div>
      </div>

      {/* LocalBusiness Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": cafeData.name,
          "image": cafeData.logo,
          "description": cafeData.description,
          "@id": "https://cafe110.co.uk",
          "url": "https://cafe110.co.uk",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": cafeData.address.street,
            "addressLocality": cafeData.address.area,
            "postalCode": cafeData.address.postcode,
            "addressCountry": "GB"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": cafeData.coordinates.lat,
            "longitude": cafeData.coordinates.lng
          },
          "openingHoursSpecification": cafeData.hours.map(h => ({
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": h.day,
            "opens": h.open.replace(' AM', ':00').replace(' PM', ':00'),
            "closes": h.close.replace(' AM', ':00').replace(' PM', ':00')
          })),
          "servesCuisine": cafeData.cuisine,
          "priceRange": cafeData.priceRange,
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": cafeData.reviews.averageRating,
            "reviewCount": cafeData.reviews.totalReviews
          }
        })
      }} />
    </footer>
  );
};
